import React, { useState } from 'react';
import { Button, Dropdown, Checkbox } from 'antd';
import { SettingOutlined, HolderOutlined } from '@ant-design/icons';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { theme } from '../../theme';
import type { UseColumnManagerResult } from '../../hooks/useColumnManager';

interface SortableRowProps {
  id: string;
  label: string;
  checked: boolean;
  disabled?: boolean;
  onToggle: () => void;
}

const SortableRow: React.FC<SortableRowProps> = ({ id, label, checked, disabled, onToggle }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  return (
    <div
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        padding: '6px 10px',
        borderBottom: `1px solid ${theme.colors.borderLight}`,
        background: isDragging ? theme.colors.backgroundGray : 'transparent',
      }}
    >
      <span
        {...attributes}
        {...listeners}
        style={{
          cursor: disabled ? 'not-allowed' : 'grab',
          color: disabled ? theme.colors.borderLight : '#c9d2dc',
          touchAction: 'none',
          display: 'flex',
        }}
      >
        <HolderOutlined />
      </span>
      <Checkbox checked={checked} disabled={disabled} onChange={onToggle} />
      <span
        style={{
          color: checked ? theme.colors.textPrimary : theme.colors.textMuted,
          fontWeight: checked ? 600 : 400,
          fontSize: '13px',
        }}
      >
        {label}
        {disabled && <span style={{ color: theme.colors.textMuted, fontWeight: 400 }}> (requerida)</span>}
      </span>
    </div>
  );
};

export interface ColumnManagerDropdownProps {
  manager: UseColumnManagerResult;
  /** Nombres amigables por clave de columna */
  labels: Record<string, string>;
  /** Claves que no se pueden ocultar (se muestran deshabilitadas) */
  requiredKeys?: string[];
  buttonSize?: 'small' | 'middle' | 'large';
}

/**
 * Dropdown compartido de "Columnas": tildar/destildar + reordenar por drag and drop.
 * Los cambios se aplican al instante — no hay botón "Aplicar".
 * Ver docs/plan_unificacion_visual.html sección "Selector de columnas".
 */
export const ColumnManagerDropdown: React.FC<ColumnManagerDropdownProps> = ({
  manager,
  labels,
  requiredKeys = [],
  buttonSize = 'small',
}) => {
  const [open, setOpen] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      manager.moveColumn(String(active.id), String(over.id));
    }
  };

  const firstHiddenIndex = manager.orderedKeys.findIndex(k => !manager.isVisible(k));

  const panel = (
    <div
      style={{
        background: '#fff',
        borderRadius: theme.borderRadius.lg,
        boxShadow: theme.styles.boxShadow,
        minWidth: '260px',
        maxHeight: '380px',
        overflowY: 'auto',
        padding: '6px 0',
      }}
      onClick={e => e.stopPropagation()}
    >
      <div style={{ padding: '4px 10px 8px', fontSize: '11px', fontWeight: 700, color: theme.colors.textMuted, letterSpacing: '.3px' }}>
        ARRASTRÁ PARA REORDENAR
      </div>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={manager.orderedKeys} strategy={verticalListSortingStrategy}>
          {manager.orderedKeys.map((key, index) => (
            <React.Fragment key={key}>
              {index === firstHiddenIndex && index > 0 && (
                <div style={{ padding: '4px 10px', fontSize: '9.5px', fontWeight: 700, color: theme.colors.textMuted, background: theme.colors.backgroundGray, letterSpacing: '.4px' }}>
                  OCULTAS
                </div>
              )}
              <SortableRow
                id={key}
                label={labels[key] || key}
                checked={manager.isVisible(key)}
                disabled={requiredKeys.includes(key)}
                onToggle={() => manager.toggleColumn(key)}
              />
            </React.Fragment>
          ))}
        </SortableContext>
      </DndContext>
      <div style={{ padding: '8px 10px 2px' }}>
        <Button type="link" size="small" onClick={manager.resetColumns} style={{ padding: 0 }}>
          Restaurar todas
        </Button>
      </div>
    </div>
  );

  return (
    <Dropdown
      menu={{ items: [{ key: 'columns', label: panel }] }}
      trigger={['click']}
      open={open}
      onOpenChange={setOpen}
    >
      <Button icon={<SettingOutlined />} size={buttonSize} style={{ borderRadius: theme.borderRadius.full }}>
        Columnas
      </Button>
    </Dropdown>
  );
};
